import { useState, useRef, useEffect } from 'react'
import { Tag, X, Plus } from 'lucide-react'
import { useAddInvoiceTag, useAllTags, useRemoveInvoiceTag } from '@/lib/api/hooks'
import { cn } from '@/lib/utils'

interface Props {
  invoiceId: string
  tags: string[]
  canEdit: boolean
  /** Compact: kis felület (pl. listán a sorban). */
  compact?: boolean
  className?: string
}

export function TagEditor({ invoiceId, tags, canEdit, compact, className }: Props) {
  const add = useAddInvoiceTag()
  const remove = useRemoveInvoiceTag()
  const all = useAllTags()
  const [input, setInput] = useState('')
  const [showSuggest, setShowSuggest] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setShowSuggest(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  async function addOne(raw: string) {
    const t = raw.trim()
    if (!t) return
    try {
      await add.mutateAsync({ invoiceId, tag: t })
      setInput('')
      setShowSuggest(false)
    } catch {
      // toast az UI page szintjén kezelt
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      void addOne(input)
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      // utolsó címke gyors törlése Backspace-szel
      remove.mutate({ invoiceId, tag: tags[tags.length - 1] })
    }
  }

  const filteredSuggestions = (all.data ?? [])
    .filter((t) => !tags.some((existing) => existing.toLowerCase() === t.toLowerCase()))
    .filter((t) => (input ? t.toLowerCase().includes(input.toLowerCase()) : true))
    .slice(0, 8)

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'relative flex flex-wrap items-center gap-1.5',
        compact ? 'min-h-[1.5rem]' : 'min-h-[2rem]',
        className,
      )}
    >
      {tags.map((t) => (
        <span
          key={t}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border bg-brand-50 text-brand-700 border-brand-100',
            'dark:bg-brand-900/30 dark:text-brand-200 dark:border-brand-800/40',
            compact ? 'px-2 py-0 text-[10px]' : 'px-2 py-0.5 text-xs',
          )}
        >
          <Tag className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          {t}
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                remove.mutate({ invoiceId, tag: t })
              }}
              aria-label={`${t} címke eltávolítása`}
              className="ml-0.5 -mr-1 rounded hover:bg-brand-100/60 dark:hover:bg-brand-800/40"
            >
              <X className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
            </button>
          )}
        </span>
      ))}

      {canEdit && !compact && (
        <div className="relative">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              setShowSuggest(true)
            }}
            onFocus={() => setShowSuggest(true)}
            onKeyDown={onKeyDown}
            placeholder={tags.length === 0 ? 'Címke hozzáadása…' : '+ címke'}
            className={cn(
              'h-7 min-w-[8rem] rounded-md border border-dashed bg-transparent px-2 text-xs',
              'border-slate-300 text-slate-700 placeholder:text-slate-400',
              'dark:border-slate-700 dark:text-slate-200 dark:placeholder:text-slate-500',
              'focus:outline-none focus:ring-1 focus:ring-brand-500',
            )}
          />
          {input.trim() && (
            <button
              type="button"
              onClick={() => void addOne(input)}
              aria-label={`'${input.trim()}' címke hozzáadása`}
              className="absolute right-0.5 top-1/2 -translate-y-1/2 rounded p-1 text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-900/30"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
          {showSuggest && filteredSuggestions.length > 0 && (
            <ul className="absolute left-0 top-full z-20 mt-1 w-48 rounded-md border border-slate-200 bg-white shadow-lg dark:bg-slate-900 dark:border-slate-700 max-h-48 overflow-y-auto scrollbar-thin">
              {filteredSuggestions.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    onClick={() => void addOne(s)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <Tag className="h-3 w-3 text-slate-400" />
                    <span>{s}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!canEdit && tags.length === 0 && (
        <span className="text-xs text-slate-400 dark:text-slate-500">— nincs címke —</span>
      )}
    </div>
  )
}
